import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import SensorScenario from "./SensorScenario";

interface ScenarioData {
    icon: React.ReactNode;
    title: string;
    description: string;
}

interface SensorContentProps {
    activeTab: string;
    tabIcon: React.ReactNode;
    tabColor: string;
    tabDescription: string;
    scenarios: ScenarioData[];
    helpTitle: string;
    isMobile: boolean;
    isRTL?: boolean;
}

const SensorContent: React.FC<SensorContentProps> = ({ activeTab, tabIcon, tabColor, tabDescription, scenarios, helpTitle, isMobile, isRTL = true }) => {
    return (
        <AnimatePresence mode="wait">
            <motion.div key={activeTab} className="w-full md:w-2/3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
                {/* عنوان */}
                <motion.div className="relative mb-4 md:mb-6" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
                    <h4 className="text-lg font-medium text-gray-800 flex items-center">
                        <motion.div
                            className={`relative inline-flex items-center justify-center ${
                                isRTL ? "ml-3" : "mr-3"
                            } w-9 h-9 mt-2.5 rounded-full bg-gradient-to-r ${tabColor} text-white overflow-hidden group`}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <motion.div
                                className="absolute inset-0 rounded-full bg-white opacity-30"
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.5, 2], opacity: [0.3, 0.2, 0] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                            />
                            <motion.div animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}>
                                {tabIcon}
                            </motion.div>
                        </motion.div>

                        <motion.span className="relative" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}>
                            <span className="relative z-10">{helpTitle}</span>
                            <motion.div
                                className={`absolute -bottom-1 ${isRTL ? "right-0" : "left-0"} h-1 bg-gradient-to-r ${tabColor} rounded-full`}
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            />
                        </motion.span>
                    </h4>
                </motion.div>

                {/* توضیحات */}
                <motion.div className={`relative mb-4 md:mb-6 ${isRTL ? "pr-4" : "pl-4"}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                    <motion.p className="text-gray-700 leading-relaxed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
                        {tabDescription}
                    </motion.p>
                    <motion.div
                        className={`absolute ${isRTL ? "-right-2" : "-left-2"} top-0 bottom-0 w-1 rounded-full bg-gradient-to-b ${tabColor} opacity-50`}
                        initial={{ height: 0 }}
                        animate={{ height: "100%" }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    />
                </motion.div>

                {/* سناریوها */}
                <motion.div className="relative" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {scenarios
                            .filter((_, index) => !isMobile || index < 2)
                            .map((scenario, index) => (
                                <motion.div key={index} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + index * 0.1 }}>
                                    <SensorScenario icon={scenario.icon} title={scenario.title} description={scenario.description} color={tabColor} isRTL={isRTL} />
                                </motion.div>
                            ))}
                    </div>

                    {/* ذرات متحرک */}
                    <motion.div className="absolute -z-10 inset-0 overflow-hidden">
                        {[...Array(5)].map((_, i) => (
                            <motion.div
                                key={i}
                                className={`absolute w-2 h-2 rounded-full bg-gradient-to-r ${tabColor} opacity-20`}
                                style={{ top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%` }}
                                animate={{ y: [0, Math.random() * -30 - 10], x: [0, Math.random() * 20 - 10], opacity: [0.2, 0], scale: [1, 0.5] }}
                                transition={{ duration: 3 + Math.random() * 2, repeat: Infinity, delay: i * 0.8, repeatDelay: Math.random() * 2 }}
                            />
                        ))}
                    </motion.div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SensorContent;
